import React from "react";
import PropTypes from "prop-types";
import { FieldArray, useFormikContext } from "formik";
import { Grid } from "@material-ui/core";
import Button from "components/common_components/Button";
import Icon from "components/common_components/Icon";
import { InputField, TextareaField } from "components/common_components/Formik";
import AssetImageUploader from "./AssetImageUploader";
import { ASSET_LIMITS } from "../utils";

const EMPTY_ASSET = {
    id: null,
    text1: "",
    text2: "",
    text3: "",
    imageUrl: "",
    onImageClickUrl: "",
};

function AssetsSection({ campaignId, Storage, disabled }) {
    const { values, setFieldValue, errors, touched } = useFormikContext();
    const assets = values.assets || [];

    const imageError = (index) => {
        const assetTouched = touched.assets?.[index]?.imageUrl;
        const assetError = errors.assets?.[index]?.imageUrl;
        return assetTouched && assetError ? assetError : null;
    };

    return (
        <FieldArray name="assets">
            {({ push, remove }) => (
                <div className="campaign-assets">
                    <div className="campaign-assets__header">
                        <div className="campaign-assets__title">
                            <h3>Assets</h3>
                            <p>Banners shown in the Client Portal dashboard carousel.</p>
                        </div>
                        {!disabled && (
                            <Button secondary small onClick={() => push({ ...EMPTY_ASSET })}>
                                <Icon name="add" size="sm" color="white" />
                                <span>Add asset</span>
                            </Button>
                        )}
                    </div>

                    {assets.length === 0 ? (
                        <div className="campaign-assets__empty">
                            <Icon name="no-results" size="lg" color="secondary" />
                            <span>No assets yet.{!disabled && " Add at least one to show the campaign in the carousel."}</span>
                        </div>
                    ) : (
                        assets.map((asset, index) => (
                            <div className="campaign-asset" key={asset.id ?? `new-${index}`}>
                                <div className="campaign-asset__head">
                                    <span className="campaign-asset__index">Asset #{index + 1}</span>
                                    {!disabled && (
                                        <Button secondary small link onClick={() => remove(index)}>
                                            <Icon name="delete" size="sm" color="secondary" />
                                            <span>Remove</span>
                                        </Button>
                                    )}
                                </div>

                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <AssetImageUploader
                                            value={asset.imageUrl}
                                            onChange={(url) => setFieldValue(`assets.${index}.imageUrl`, url)}
                                            campaignId={campaignId}
                                            Storage={Storage}
                                            disabled={disabled}
                                        />
                                        {imageError(index) && <div className="campaign-asset__image-error">{imageError(index)}</div>}
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextareaField
                                            fieldName={`assets.${index}.text1`}
                                            label="Title"
                                            maxLength={ASSET_LIMITS.text1}
                                            rows={2}
                                            disabled={disabled}
                                        />
                                        <TextareaField
                                            fieldName={`assets.${index}.text2`}
                                            label="Subtitle"
                                            maxLength={ASSET_LIMITS.text2}
                                            rows={2}
                                            disabled={disabled}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextareaField
                                            fieldName={`assets.${index}.text3`}
                                            label="Detail"
                                            maxLength={ASSET_LIMITS.text3}
                                            rows={3}
                                            disabled={disabled}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <InputField
                                            fieldName={`assets.${index}.onImageClickUrl`}
                                            label="Image Click URL"
                                            maxLength={ASSET_LIMITS.onImageClickUrl}
                                            disabled={disabled}
                                        />
                                    </Grid>
                                </Grid>
                            </div>
                        ))
                    )}
                </div>
            )}
        </FieldArray>
    );
}

AssetsSection.propTypes = {
    campaignId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    Storage: PropTypes.shape({}).isRequired,
    disabled: PropTypes.bool,
};

AssetsSection.defaultProps = {
    disabled: false,
};

export default AssetsSection;
